import React from 'react';
import { Tag, Layers, Printer, HelpCircle } from 'lucide-react';
import { TagBrand, TagCompanyPill, TagUserMenu } from './TagChrome';
import { tagThemeClass } from './tagTheme';

// --- Concept A: Context Bar (sticky topbar + company pill) -----------------
function HeaderA({ principal, companyName, isTagAdmin, onLogout }) {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70" data-testid="tagws-header-a">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <TagBrand />
        <TagCompanyPill companyName={companyName} />
        <div className="flex items-center gap-1">
          <TagUserMenu principal={principal} companyName={companyName} isTagAdmin={isTagAdmin} onLogout={onLogout} />
        </div>
      </div>
    </header>
  );
}

// --- Concept B: Split Header (brand row + utility/toolbar row) --------------
function HeaderB({ principal, companyName, isTagAdmin, onLogout, toolbar }) {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur" data-testid="tagws-header-b">
      <div className="mx-auto flex h-12 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <TagBrand />
          <TagCompanyPill companyName={companyName} />
        </div>
        <div className="flex items-center gap-1">
          <TagUserMenu principal={principal} companyName={companyName} isTagAdmin={isTagAdmin} onLogout={onLogout} />
        </div>
      </div>
      <div className="border-t border-border bg-background/60">
        <div className="mx-auto max-w-7xl px-4 py-2.5 sm:px-6 lg:px-8">{toolbar}</div>
      </div>
    </header>
  );
}

// --- Concept C: Rail + Topbar (icon rail for print workflow) ----------------
function HeaderC({ principal, companyName, isTagAdmin, onLogout }) {
  const railItems = [
    { icon: Layers, label: 'Jewellery', id: 'jewellery', active: true },
    { icon: Printer, label: 'Print', id: 'print', active: false },
    { icon: HelpCircle, label: 'Help', id: 'help', active: false },
  ];
  return (
    <>
      <aside className="fixed left-0 top-0 z-40 hidden h-full w-16 flex-col items-center gap-1 border-r border-border bg-card py-3 md:flex" data-testid="tagws-nav-rail">
        <span className="mb-3 grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground"><Tag size={18} /></span>
        {railItems.map((it) => {
          const I = it.icon;
          return (
            <button
              key={it.id} title={it.label} aria-label={it.label} data-testid={`tagws-nav-rail-${it.id}`}
              className={`grid h-10 w-10 place-items-center rounded-lg transition-colors duration-150 ${it.active ? 'bg-secondary text-foreground ring-1 ring-[hsl(var(--ring))]/40' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}
            >
              <I size={18} />
            </button>
          );
        })}
      </aside>
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur md:pl-16" data-testid="tagws-header-c">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3 md:hidden"><TagBrand /></div>
          <TagCompanyPill companyName={companyName} />
          <div className="ml-auto flex items-center gap-1">
            <TagUserMenu principal={principal} companyName={companyName} isTagAdmin={isTagAdmin} onLogout={onLogout} />
          </div>
        </div>
      </header>
    </>
  );
}

/**
 * TagWorkspaceShell — the neutral white-label chrome for rosentiquestag.com.
 * Wraps everything in `.tag-scope` + the chosen colour theme so the tokens
 * apply and never leak into the Inventique portal or Notes module. The header
 * concept is swappable (a | b | c).
 */
export function TagWorkspaceShell({
  variant = 'a', principal, companyName, isTagAdmin, onLogout, toolbar, children,
}) {
  const v = (variant || 'a').toLowerCase();
  const chrome = { principal, companyName, isTagAdmin, onLogout };
  return (
    <div className={`tag-scope ${tagThemeClass()} min-h-screen bg-background text-foreground`} data-testid="tagws-shell">
      {v === 'b' ? <HeaderB {...chrome} toolbar={toolbar} />
        : v === 'c' ? <HeaderC {...chrome} />
          : <HeaderA {...chrome} />}

      <div className={v === 'c' ? 'md:pl-16' : ''}>
        {v !== 'b' && toolbar ? (
          <div className="sticky top-14 z-20 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
            <div className="mx-auto max-w-7xl px-4 py-2.5 sm:px-6 lg:px-8">{toolbar}</div>
          </div>
        ) : null}
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
